import { readPreviewTeam } from '../service/previewStorage';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';
import isEmpty from 'lodash/isEmpty';
import { useLocation, useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import Joyride, { STATUS } from 'react-joyride';
import { Slide } from 'react-toastify';
import { isAuth, isAuthAsWaited, getUser, isClubSwitching } from '../redux/selector/User';
import { isRightPanelOpen } from '../redux/selector/Sidebar';

import { isYouthCampAvailable } from '../redux/selector/YouthCamp';
import { getTourState } from '../redux/selector/TourControl';
import {
  testAuth,
  setClubSwitchLoading,
  getLeagueName,
  previewLogin,
} from '../redux/actions/User';
import { allowTour } from '../redux/actions/TourControl';
import { getYouthCampStatusRequest } from '../redux/actions/YouthCamp';
import { tourSteps } from '../helpers/tour/tourSteps';
import {
  locationToStorage,
  skipAllTours,
  tourStorageValue,
} from '../helpers/tour/locationToStorage';
import {
  SQUAD_LOCATION,
  PLAYER_INFO_STORAGE,
  STRATEGY_LOCATION,
} from '../helpers/tour/constants';

import { StyledContainerLoader, StyledLoader } from '../page/styled';
import { StyledToastContainer } from '../globalStyled';
import TourTooltip from '../components/TourTooltip';
import 'react-toastify/dist/ReactToastify.css';

export default function AuthLayout({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const isAuthenticated = useSelector(isAuth);
  const isAuthedAsWaiter = useSelector(isAuthAsWaited);
  const { leagueName, avatarID, username, authError } = useSelector(getUser);
  const isTourStart = useSelector(getTourState);
  const isYouthCampAuth = useSelector(isYouthCampAvailable);
  const isSidebarOpen = useSelector(isRightPanelOpen);
  const clubSwitchLoading = useSelector(isClubSwitching);

  const [activeTour, setActiveTour] = useState([]);
  const skipAllRef = useRef(false);
  const discordData = username && avatarID;
  const previewTeam = readPreviewTeam();
  const isLoginCallback = location.pathname.startsWith('/login/');
  const sessionPending =
    isAuthenticated === null && !isAuthedAsWaiter && !previewTeam && !isLoginCallback;
  const isPublicSeoPath =
    location.pathname === '/' ||
    isLoginCallback ||
    location.pathname === '/blog' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname === '/press-kit' ||
    location.pathname.startsWith('/user-guide') ||
    location.pathname === '/privacy' ||
    location.pathname === '/terms' ||
    location.pathname === '/delete-account' ||
    location.pathname === '/signup';
  const isAppLoading = !isPublicSeoPath && sessionPending;

  useEffect(() => {
    if (previewTeam) {
      dispatch(previewLogin(previewTeam.id, previewTeam.name));
    }
  }, []);

  useEffect(() => {
    // Discord callback owns session creation via POST /login — a parallel
    // GET /login here races the Set-Cookie and used to bounce users to /.
    if (isLoginCallback) return;
    if (location.pathname !== '/') {
      if (isAuthenticated === null && !isAuthedAsWaiter && !authError && !previewTeam) {
        dispatch(testAuth());
      }
      if (isAuthenticated) {
        if (isYouthCampAuth === null) {
          dispatch(getYouthCampStatusRequest());
        }
        if (leagueName === null) {
          dispatch(getLeagueName());
        }
      }
    }
  }, [
    dispatch,
    isAuthenticated,
    isAuthedAsWaiter,
    authError,
    isYouthCampAuth,
    discordData,
    leagueName,
    location,
    history,
    isLoginCallback,
    previewTeam,
  ]);
  useEffect(() => {
    if (location.pathname === '/' && isAuthedAsWaiter) {
      history.push('/signup');
    }
  }, [history, isAuthedAsWaiter, location.pathname]);

  useEffect(() => {
    if (tourStorageValue[location.pathname]) {
      const activeLocalStorageTourState = isSidebarOpen
        ? localStorage.getItem(PLAYER_INFO_STORAGE)
        : localStorage.getItem(tourStorageValue[location.pathname]);
      if (!activeLocalStorageTourState && isTourStart) {
        setTimeout(() => {
          setActiveTour(tourSteps(location.pathname, isSidebarOpen, isMobile));
        }, 1000);
      }
      if (activeLocalStorageTourState && isTourStart) {
        dispatch(allowTour(false));
      }
    }
  }, [location, isTourStart, dispatch, isSidebarOpen]);

  const handleJoyrideCallback = (callbackData) => {
    if (callbackData.status === STATUS.SKIPPED) {
      if (skipAllRef.current) {
        skipAllTours();
      } else {
        locationToStorage(location.pathname, isSidebarOpen);
      }
      skipAllRef.current = false;
      dispatch(allowTour(false));
      setActiveTour([]);
      return;
    }
    if (callbackData.status === STATUS.FINISHED) {
      dispatch(allowTour(false));
      locationToStorage(location.pathname, isSidebarOpen);
      setActiveTour([]);
    }
  };
  const showAppLoader = isAppLoading || clubSwitchLoading;
  const authRetries = useRef(0);
  useEffect(() => {
    if (!sessionPending || !authError) return;
    if (authRetries.current >= 5) {
      // Cookie may still be valid — leave isAuth pending. Never force
      // isAuth:false (that locked users out onto a fake logged-out landing).
      if (!isPublicSeoPath) {
        history.replace('/');
      }
      return;
    }
    const timer = setTimeout(() => {
      authRetries.current += 1;
      dispatch(testAuth());
    }, 1000 * (authRetries.current + 1));
    return () => clearTimeout(timer);
  }, [authError, sessionPending, dispatch, history, isPublicSeoPath]);
  useEffect(() => {
    if (isAuthenticated) authRetries.current = 0;
    const reconnect = () => {
      if (authError) {
        authRetries.current = 0;
        dispatch(testAuth());
      }
    };
    window.addEventListener('online', reconnect);
    return () => window.removeEventListener('online', reconnect);
  }, [authError, isAuthenticated, dispatch]);
  useEffect(() => {
    if (!clubSwitchLoading) return;
    const timer = setTimeout(() => {
      dispatch(setClubSwitchLoading(false));
      dispatch(testAuth());
    }, 25000);
    return () => clearTimeout(timer);
  }, [clubSwitchLoading, dispatch]);

  return (
    <>
      <StyledToastContainer
        className="full-width-toast-container"
        toastClassName="full-width-toast"
        closeButton
        position="top-right"
        autoClose={5000}
        transition={Slide}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnVisibilityChange={false}
        pauseOnHover
      />
      {showAppLoader ? (
        <StyledContainerLoader>
          <StyledLoader
            type="Bars"
            color="var(--tol-header-accent)"
            height={100}
            width={100}
          />
        </StyledContainerLoader>
      ) : (
        <>
          {!isEmpty(activeTour) && (
            <Joyride
              disableScrollParentFix={location.pathname === STRATEGY_LOCATION}
              floaterProps={{
                disableAnimation: true,
                styles: {
                  arrow: { color: theme.card },
                  floater: {
                    filter: 'drop-shadow(0 12px 28px rgba(0, 0, 0, 0.22))',
                  },
                },
              }}
              disableScrolling={!(location.pathname === SQUAD_LOCATION && isSidebarOpen)}
              callback={(data) => handleJoyrideCallback(data)}
              run={!isEmpty(activeTour) && isTourStart}
              steps={activeTour}
              continuous
              showSkipButton
              tooltipComponent={(tooltipData) => (
                <TourTooltip
                  {...tooltipData}
                  onSkipAll={(event) => {
                    skipAllRef.current = true;
                    tooltipData.skipProps.onClick(event);
                  }}
                />
              )}
              styles={{
                options: {
                  zIndex: 10000,
                  arrowColor: theme.card,
                  backgroundColor: theme.card,
                  overlayColor: theme.isDark
                    ? 'rgba(0, 0, 0, 0.55)'
                    : 'rgba(0, 0, 0, 0.4)',
                },
                tooltip: {
                  padding: 0,
                  backgroundColor: 'transparent',
                  borderRadius: 16,
                },
              }}
            />
          )}
          {children}
        </>
      )}
    </>
  );
}
